import React from 'react'
import cx from 'classnames'
import { Link } from 'react-router'
import moment from 'moment'

import { Badge, RedBadge, YellowBadge, TealBadge, DataCard, DataCardHeader, OptionsMenu } from '../data-card/data-card-parts'

class ApprovalMessage extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      expanded: false
    }
    this.toggleExpand = this.toggleExpand.bind(this)
    this.versionToString = this.versionToString.bind(this)
  }

  toggleExpand() {
    this.setState({ expanded: !this.state.expanded })
  }

  versionToString(version, autoApprovable) {
    const statusString = {
      approve: 'Approved',
      complete: 'Completed',
      mark_as_open: 'Marked As Corrective Action',
      unsatisfactory_reschedule: 'Unsatisfactory Reschedule',
      reschedule: 'Rescheduled',
      mark_as_delayed: 'Marked as Delayed'
    }[version.eventName] || ''

    // Commented: For task "AraHull softaware question", just a possible hack until we can rebuild paper-trail
    // const responsibleName = version.eventName === 'complete' ? version.technicianName : version.requestorName
    const responsibleName = version.requestorName

    let versionString = `${statusString} on ${moment(version.createdAt).format('MMMM D, YYYY [at] h:mm:ss A')}`
    if (!autoApprovable || version.eventName == 'complete' || version.eventName == 'mark_as_delayed') {
      versionString += ` by ${responsibleName}`
    }

    return versionString
  }

  render() {
    const { approvalStatus, approvedAt, approvingUserName, servicingVersions } = this.props.servicing
    const { autoApprovable } = this.props
    const { expanded = false } = this.state

    const [lastVersion] = servicingVersions

    if (lastVersion && lastVersion.inspectedAt) {
      return (
        <div className="expandable">
          <img onClick={this.toggleExpand} className={cx('toggle-button', { '_up': expanded })} src={require('../../images/arrow-down.svg')} />
          <p className='timestamp'>
            {`Inspected on ${moment(lastVersion.inspectedAt).format('MMMM D, YYYY [at] h:mm A')}`}
            {!autoApprovable ? ` by ${lastVersion.requestorName}` : ''}
          </p>
          {expanded &&
            <div style={{ lineHeight: '0.8em', fontSize: 'small', fontStyle: 'italic', color: 'rgb(40,40,40, 0.6)' }}>
              {servicingVersions.map((version, idx) => (
                <p className="timestamp" key={idx}>
                  {this.versionToString(version, autoApprovable)}
                </p>
              ))}
            </div>
          }
        </div>
      )
    }

    if (approvalStatus !== 'approved' || !approvedAt || !approvingUserName) { return null }

    return (<p className='timestamp'>
      {`Approved on ${approvedAt.format('MMMM D, YYYY [at] h:mm A')} by ${approvingUserName}`}
    </p>)
  }
}

class ServicingSummary extends React.Component {
  approveServicing(servicing) {
    if (servicing && servicing.sameDayIssueCount > 0) {
      this.props.onRequireApproval()
    } else {
      this.props.onApprove()
    }
  }

  hasImageBadge({ id, beforeAfters, beforeImage, afterImage }) {
    //sls:
    if (beforeAfters === "" && afterImage === "")
      return null;

    if (beforeAfters.length == 0 && beforeImage.url == null && afterImage.url == null) return null


    return (
      <Link to={`${window.location.pathname}/${id}/images`}>
        <TealBadge right text="View Images" />
      </Link>
    )
  }

  hasInspectionImageBadge({ id, inspectionImageA = {}, inspectionImageB = {} }) {
    if (inspectionImageA.url === null && inspectionImageB.url === null) return null

    return (
      <Link to={`${window.location.pathname}/${id}/redo_images`}>
        <RedBadge additionalClasses="right" text="Inspection Images" />
      </Link>
    )
  }

  wasDeletedBadge({ id, deletedAt }) {
    if (deletedAt === null) return null

    return (
      <RedBadge additionalClasses="right" text="DELETED" />
    )
  }

  issueBadge(servicing) {
    if (servicing.sameDayIssueCount == 0) return

    const pluralizedIssues = servicing.sameDayIssueCount > 1 ? 'Issues' : 'Issue'
    return (
      <Link to={`summaries/${servicing.workOrderId}/${servicing.id}/issues`}>
        <Badge color="red" text={`${servicing.sameDayIssueCount} ${pluralizedIssues}`} />
      </Link>
    )
  }

  anyServicesCompleted() {
    const { services } = this.props
    if (!services) return false
    return services.some(s => s.completedAt !== null)
  }

  completedServices() {
    const { services } = this.props
    if (!services) return []
    return services.filter(s => s.completedAt !== null)
  }

  addedServices() {
    const { services } = this.props
    if (!services) return []
    return services.filter(s => s.completedAt === null)
  }

  formatCreation(servicing, inputtedBy) {
    if (inputtedBy.name && servicing.createdAt) {
      return `Inputted by ${inputtedBy.name} on ${servicing.createdAt.format('MMMM D, YYYY [at] h:mm A')}`
    }
  }

  formatDeletion(servicing, deletedBy) {
    if (deletedBy.name && servicing.deletedAt) {
      return `Deleted by ${deletedBy.name} on ${servicing.deletedAt.format('MMMM D, YYYY [at] h:mm A')}`
    }
  }

  formatCompletion(servicing, completedBy) {
    if (!servicing.completedAt) {
      // indicate if services were added
      if (this.anyServicesCompleted()) {
        const completedServices = this.completedServices()
          .map(s => (`${s.name} completed on ${s.completedAt.format('MMMM D, YYYY [at] h:mm A')}`))
        const addedServices = this.addedServices()
          .map(s => (`${s.name} added`))
        return completedServices.join(', ') + ' ' + addedServices.join(', ')
      }
      return 'Yet to be completed'
    }

    var base = {
      open: 'Last completed on',
      completed: 'Completed on',
      unable: 'Marked unable to complete on',
      permanently_occupied: 'Marked permanently occupied on',
      delayed: 'Delayed on'
    }[servicing.status]

    base += ` ${servicing.completedAt.format('MMMM D, YYYY [at] h:mm A')} by ${completedBy.name}`

    return base
  }

  servicingStatusImage(servicing, status, imageName) {
    return (
      <img src={
        servicing.approvalStatus === status
          ? require(`../../images/task-${imageName}-selected.svg`)
          : require(`../../images/task-${imageName}.svg`)
      }
      />
    )
  }

  formatReason(servicing) {
    if (servicing.unableReason && servicing.unableReason.length > 0) {
      return <p className='timestamp'>Unable to complete reason: {servicing.unableReason}</p>
    }

    if (servicing.redoReason && servicing.redoReason.length > 0) {
      let date = (servicing.redoneAt !== null) ? ` (${servicing.redoneAt.format('hh:mmA')})` : ''
      return <p className='timestamp'>Redo reason: {`${servicing.redoReason}${date}`}</p>
    }
  }

  onMarkAsOpen(servicing, canAddServices) {
    if (servicing.status === 'completed') {
      this.props.requireOpenRedoReason(canAddServices)
    } else {
      this.props.onMarkAsOpen()
    }
  }

  onReschedule(servicing, canAddServices) {
    if (servicing.status === 'completed') {
      this.props.requireRescheduledRedoReason(canAddServices)
    } else {
      this.props.onReschedule()
    }
  }

  formatPinnedComment(servicing) {
    if (servicing.archiveComment) {
      return (
        <p className='timestamp'>
          Pinned Comment: {servicing.archiveComment}
        </p>
      )
    }
  }

  render() {
    const {
      servicing = {},
      services = [],
      area = {},
      completedBy = {},
      pinnedComment = {},
      inputtedBy = {},
      deletedBy = {},

      readonly,
      canApprove,
      canAddServices,
      canReopen,
      canShowInputted,
      onApprove,
      onRequireApproval,
      onReschedule,
      onMarkAsOpen,
      onDeny
    } = this.props

    const permissions = servicing.permissions

    const canApproveServicing = !readonly || canApprove || servicing.approvalStatus === 'approved'
    const canDenyServicing = !readonly || servicing.approvalStatus === 'denied'
    const canRescheduleServicing = !readonly || servicing.approvalStatus === 'rescheduled'
    const canAddServicesToServicing = servicing.status === 'completed' && servicing.approvalStatus === null && canAddServices
    // console.log(`canAddServices: ${canAddServices}`)
    // console.log(`canAddServicesToServicing: ${canAddServicesToServicing}`)

    let highPriorityBadge = null

    if (servicing.highPriority) {
      highPriorityBadge = <RedBadge text='high priority' />
    }

    // Servicing Card options menu
    // let options = []
    // if (canAddServicesToServicing) {
    //  options.push({ name: 'Add services', link: `/in-progress/${servicing.workOrderId}/add-services/${servicing.id}` })
    // }

    return (
      <DataCard className={cx('servicing-summary separated', { readonly })}>
        <div className='details'>
          {/*<OptionsMenu optionItems={options} />*/}
          <strong>{area.name}</strong> {this.issueBadge(servicing)}
          {highPriorityBadge}
          {servicing.redone && <RedBadge text="redo" additionalClasses="offset-left" />}
          {servicing.requireImages &&
            <RedBadge text="Images Required" additionalClasses="offset-left" />}
          {this.hasImageBadge(servicing)}
          {this.hasInspectionImageBadge(servicing)}
          {this.wasDeletedBadge(servicing)}

          <div className='services'>
            {services.map(({ id, name }) => <p key={id}>{name} <small>({servicing.servicesServicings[id].quantity}x)</small></p>)}
          </div>

          {canShowInputted && <p className='timestamp'>{this.formatCreation(servicing, inputtedBy)}</p>}
          <p className='timestamp'>{this.formatCompletion(servicing, completedBy)}</p>
          {canShowInputted && <p className='timestamp'>{this.formatDeletion(servicing, deletedBy)}</p>}
          <ApprovalMessage servicing={servicing} />
          {this.formatPinnedComment(servicing)}
          {this.formatReason(servicing)}
        </div>

        <div className='controls'>
          {canAddServicesToServicing && permissions.canAddServices &&
            <Link to={`/in-progress/${servicing.workOrderId}/add-services/${servicing.id}`}>
              <img src={require('../../images/plus-icon.svg')} />
            </Link>
          }

          {canApproveServicing && permissions.canApprove &&
            <a onClick={() => (!readonly || canApprove) && this.approveServicing(servicing)}>
              {this.servicingStatusImage(servicing, 'approved', 'completed')}
            </a>
          }

          {canDenyServicing && permissions.canDeny &&
            <a onClick={() => !readonly && onDeny(event)}>
              {this.servicingStatusImage(servicing, 'denied', 'cant-complete')}
            </a>
          }

          {canRescheduleServicing && permissions.canReschedule &&
            <a onClick={() => !readonly && this.onReschedule(servicing, canAddServices)}>
              {this.servicingStatusImage(servicing, 'rescheduled', 'rescheduled')}
            </a>
          }

          {canReopen && servicing.status !== 'open' &&
            <a onClick={() => this.onMarkAsOpen(servicing, canAddServices)}>
              <img src={
                servicing.status === 'open'
                  ? require('../../images/task-rescheduled-selected.svg')
                  : require('../../images/task-rescheduled.svg')
              }
              />
            </a>
          }
        </div>
      </DataCard>
    )
  }
}

export default ServicingSummary



// WEBPACK FOOTER //
// ./src/components/servicings/servicing-summary.js